import { useState } from 'react';
import { Search, Filter, ArrowUpDown, SortAsc, SortDesc, X } from 'lucide-react';
import type { EventTag, EventTeamMember } from '../../types/eventTeam';
import { cn } from '../../utils/cn';
import { useIsMobile } from '../../hooks/useIsMobile';

function formatLabel(s: string) {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface EventFiltersState {
  search: string;
  status: string;
  category: string;
  eventType: string;
  tagId: string;
  assignee: string;
  priorityMin: string;
  priorityMax: string;
  scheduledAfter: string;
  scheduledBefore: string;
  dueAfter: string;
  dueBefore: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export const EMPTY_EVENT_FILTERS: EventFiltersState = {
  search: '',
  status: '',
  category: '',
  eventType: '',
  tagId: '',
  assignee: '',
  priorityMin: '',
  priorityMax: '',
  scheduledAfter: '',
  scheduledBefore: '',
  dueAfter: '',
  dueBefore: '',
  sortBy: 'created_at',
  sortOrder: 'desc',
};

interface EventFiltersProps {
  variant: 'events' | 'projects';
  filters: EventFiltersState;
  onChange: (next: EventFiltersState) => void;
  statuses: string[];
  tags: EventTag[];
  /** Roster members for the assignee filter (`assignee` = roster member id — guide §1.3). */
  members: EventTeamMember[];
  /** Set false to disable everything except status (kanban-summary source — guide §1.3). */
  advancedEnabled?: boolean;
}

/** Full filter/sort bar for the events & projects list endpoints (guide §1.3 / §1.6). */
export const EventFilters = ({
  variant,
  filters,
  onChange,
  statuses,
  tags,
  members,
  advancedEnabled = true,
}: EventFiltersProps) => {
  const isMobile = useIsMobile();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (patch: Partial<EventFiltersState>) => onChange({ ...filters, ...patch });

  const hasActiveFilters =
    filters.status ||
    filters.category ||
    filters.eventType ||
    filters.tagId ||
    filters.assignee ||
    filters.priorityMin ||
    filters.priorityMax ||
    filters.scheduledAfter ||
    filters.scheduledBefore ||
    filters.dueAfter ||
    filters.dueBefore;

  const clearFilters = () => onChange({ ...EMPTY_EVENT_FILTERS, sortBy: filters.sortBy, sortOrder: filters.sortOrder });

  const sortOptions =
    variant === 'events'
      ? [
          ['created_at', 'Date Created'],
          ['updated_at', 'Last Updated'],
          ['priority', 'Priority'],
          ['status', 'Status'],
          ['due_date', 'Due Date'],
          ['scheduled_start', 'Scheduled Start'],
          ['title', 'Title'],
        ]
      : [
          ['created_at', 'Date Created'],
          ['updated_at', 'Last Updated'],
          ['priority', 'Priority'],
          ['status', 'Status'],
          ['due_date', 'Due Date'],
          ['title', 'Title'],
        ];

  return (
    <div className="glass-card p-3 space-y-3">
      <div className={cn('grid gap-2', isMobile ? 'grid-cols-1' : 'grid-cols-12')}>
        <div className={cn('relative', isMobile ? '' : 'col-span-5')}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            className="glass-input w-full pl-9"
            placeholder={variant === 'events' ? 'Search events…' : 'Search projects…'}
            disabled={!advancedEnabled}
          />
        </div>

        <div className={cn('relative', isMobile ? '' : 'col-span-3')}>
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <select
            value={filters.status}
            onChange={(e) => set({ status: e.target.value })}
            className="glass-input w-full pl-9"
          >
            <option value="">All Statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {formatLabel(s)}
              </option>
            ))}
          </select>
        </div>

        <div className={cn('relative', isMobile ? '' : 'col-span-3')}>
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <select
            value={filters.sortBy}
            onChange={(e) => set({ sortBy: e.target.value })}
            className="glass-input w-full pl-9"
            disabled={!advancedEnabled}
          >
            {sortOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className={cn('flex items-center gap-1', isMobile ? '' : 'col-span-1')}>
          <button
            onClick={() => set({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
            className="btn-icon"
            title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            aria-label={filters.sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
            type="button"
            disabled={!advancedEnabled}
          >
            {filters.sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          disabled={!advancedEnabled}
          className={cn(
            'btn-ghost btn-xs',
            !advancedEnabled ? 'cursor-not-allowed' : showAdvanced && 'text-accent-300',
          )}
          aria-expanded={showAdvanced}
          title={!advancedEnabled ? 'Advanced filters are unavailable in the workspace-counts view' : undefined}
        >
          {showAdvanced ? 'Hide filters' : 'More filters'}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="btn-ghost btn-xs text-red-400 hover:text-red-300"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>

      {showAdvanced && advancedEnabled && (
        <div className={cn('grid gap-3 pt-1 border-t border-glass-200/10', isMobile ? 'grid-cols-1' : 'grid-cols-4')}>
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Category</label>
            <input
              type="text"
              value={filters.category}
              onChange={(e) => set({ category: e.target.value })}
              className="glass-input w-full text-sm"
              placeholder="e.g. minigame"
            />
          </div>

          {variant === 'events' && (
            <div>
              <label className="text-xs font-medium text-glass-400 mb-1.5 block">Event type</label>
              <select
                value={filters.eventType}
                onChange={(e) => set({ eventType: e.target.value })}
                className="glass-input w-full text-sm"
              >
                <option value="">Any type</option>
                <option value="one_off">One Off</option>
                <option value="recurring">Recurring</option>
                <option value="multi_stage">Multi Stage</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Tag</label>
            <select
              value={filters.tagId}
              onChange={(e) => set({ tagId: e.target.value })}
              className="glass-input w-full text-sm"
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Assignee (roster member)</label>
            <select
              value={filters.assignee}
              onChange={(e) => set({ assignee: e.target.value })}
              className="glass-input w-full text-sm"
            >
              <option value="">Anyone</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Min Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={filters.priorityMin}
              onChange={(e) => set({ priorityMin: e.target.value })}
              className="glass-input w-full text-sm"
              placeholder="1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Max Priority</label>
            <input
              type="number"
              min={1}
              max={10}
              value={filters.priorityMax}
              onChange={(e) => set({ priorityMax: e.target.value })}
              className="glass-input w-full text-sm"
              placeholder="10"
            />
          </div>

          {variant === 'events' && (
            <>
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Scheduled after</label>
                <input
                  type="datetime-local"
                  value={filters.scheduledAfter}
                  onChange={(e) => set({ scheduledAfter: e.target.value })}
                  className="glass-input w-full text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Scheduled before</label>
                <input
                  type="datetime-local"
                  value={filters.scheduledBefore}
                  onChange={(e) => set({ scheduledBefore: e.target.value })}
                  className="glass-input w-full text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Due after</label>
                <input
                  type="datetime-local"
                  value={filters.dueAfter}
                  onChange={(e) => set({ dueAfter: e.target.value })}
                  className="glass-input w-full text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Due before</label>
                <input
                  type="datetime-local"
                  value={filters.dueBefore}
                  onChange={(e) => set({ dueBefore: e.target.value })}
                  className="glass-input w-full text-sm"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
